import EntityProperty from '@/common/models/EntityProperty';
import { generateUUID } from '@/globals';

export interface IBackgroundOperation<T = unknown> {
  action: () => Promise<void>;
  batch: T[];
  didSucceed: boolean;
}

export default class BackgroundOperationsBatch<T = unknown> {
  id: string;

  description: string;

  operations: IBackgroundOperation<T>[];

  routeToListView: string;

  idEntityProperty: EntityProperty<T>;

  errorMessage: string;

  onCompleteCallback: ((callback: { wasSuccessful: boolean }) => void) | null;

  executionIndexInternal: number;

  showProgress: boolean;

  waitTimeAfterCompletion: number;

  constructor(
    description: string,
    operations: IBackgroundOperation<T>[],
    routeToListView: string,
    idEntityProperty: EntityProperty<T>,
    errorMessage: string,
    onCompleteCallback: ((callback: { wasSuccessful: boolean }) => void) | null = null,
    showProgress = true,
    waitTimeAfterCompletion = 2000,
  ) {
    this.id = generateUUID();
    this.description = description; // Description of the task being executed (e.g.: "Updating line items...")
    this.operations = operations; // List of operations needed to be executed.
    this.routeToListView = routeToListView;
    this.idEntityProperty = idEntityProperty;
    this.errorMessage = errorMessage;
    this.onCompleteCallback = onCompleteCallback;
    this.executionIndexInternal = 0; // Index of the operation being executed.
    this.showProgress = showProgress;
    this.waitTimeAfterCompletion = waitTimeAfterCompletion;
  }

  get nextOperationToExecute() {
    return this.operations[this.executionIndexInternal];
  }

  get isComplete() {
    return this.executionIndexInternal >= this.operations.length;
  }

  get executionIndex() {
    return this.executionIndexInternal;
  }

  set executionIndex(val) {
    this.executionIndexInternal = val;
    if (this.onCompleteCallback != null && this.isComplete) {
      this.onCompleteCallback({ wasSuccessful: this.operations.every((operation) => operation.didSucceed) });
    }
  }

  get allRecords() {
    return this.operations.map((x) => x.batch).flat();
  }

  get recordsAffectedByOperationFailures() {
    const batches = this.operations.filter((x) => x.didSucceed === false).map((x) => x.batch);
    return batches.flat();
  }

  get routeToRecordsAffectedByOperationFailures() {
    if (this.idEntityProperty == null || this.routeToListView == null) return null;

    const existingIDs = this.recordsAffectedByOperationFailures
      .map((x) => x[this.idEntityProperty.editablePropertyName])
      .filter((x) => x > -1);
    // No link when only dealing with new entities.
    if (existingIDs.length === 0) return null;

    return `${this.routeToListView}?filters=${this.idEntityProperty.property}~eq~${existingIDs.join(',')}`;
  }
}
